import { SectionGroup } from '@sleepstation/components'
import { ErrorReporterContext } from '@sleepstation/components-context'
import { VLayout } from '@sleepstation/components-layouts'
import { CopyRenderer } from '@sleepstation/components-markdown-renderer'
import { LAYOUT_ALIGN } from '@sleepstation/const'
import { parseToAst } from '@sleepstation/markdown'
import { propEq } from 'ramda'
import React, { useContext, useLayoutEffect } from 'react'
import { useRouteError } from 'react-router'
import { useMount } from 'react-use'
import styled from 'styled-components'
import NotFoundError from '../../../errors/NotFoundError'
import PageError from '../../../errors/PageError'
import PageNavContainer from '../../../state/PageNavContainer'
import BreakoutModuleWide from '../../shared/breakoutModules/BreakoutModuleWide'

// -----------------------------------------------------------------------------
// Utils
// -----------------------------------------------------------------------------

const getIs404 = propEq(404, `status`)

const getContent = (error) => {
  const is404 = getIs404(error)

  return {
    pageId: `errorPage`,
    title: is404 ? `Page not found` : `There was a problem loading this page`,
    message: is404
      ? `There is no page at **${window.location.pathname}**.`
      : `Our engineers have been informed and will address the problem.`,
  }
}

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

const Layout = styled(VLayout).attrs({
  align: LAYOUT_ALIGN.CENTER,
  'data-cy': `not-found`,
})``

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const ErrorPageModule = () => {
  const errorReporter = useContext(ErrorReporterContext)
  const error = useRouteError()
  const { title, pageId, message } = getContent(error)
  const messageAst = parseToAst(message)

  const [, setNav] = PageNavContainer.useContainer()

  useLayoutEffect(() => {
    setNav({
      pageId,
      backgroundIsVisible: true,
    })
  }, [setNav, pageId])

  useMount(() => {
    const reportedError = getIs404(error)
      ? new NotFoundError()
      : new PageError(error)

    errorReporter.send(reportedError, {
      reporterCalledFrom: `ErrorPageModule`,
    })
  })

  return (
    <BreakoutModuleWide
      hasGradient={false}
      hasBottomBorder={false}
      paddingV={5}
    >
      <Layout>
        <SectionGroup title={title} align={LAYOUT_ALIGN.CENTER}>
          <CopyRenderer htmlAst={messageAst} />
        </SectionGroup>
      </Layout>
    </BreakoutModuleWide>
  )
}

export default ErrorPageModule
